export interface OsVersionUpdateSettingVersion {
  version: string;
  startDateMS: number;
}

export interface OsVersionUpdateSettings {
  minRequiredNativeVersion: OsVersionUpdateSettingVersion;
  recommendedNativeVersion: OsVersionUpdateSettingVersion;
  minRequiredPublishedTimestamp: number;
}

export enum FeatureToggles {
  exampleFeature01 = "exampleFeature01",
  disableTrends = "disableTrends",
  enableDeepInviteLinks = "enableDeepInviteLinks",
  enableStopClockMode = "enableStopClockMode",
  teamAlertsChannel = "teamAlertsChannel",
  teamDisableNonStaffReplies = "teamDisableNonStaffReplies",
  blockAndMuteUsers = "blockAndMuteUsers",
  fieldScheduler = "fieldScheduler",
  // Security pass 2026-09: when ON, parent-invoice checkout rejects client/server payment
  // mismatches. When OFF (default) mismatches are only logged (shadow mode) so the recompute can
  // be validated against real traffic before it can block a family's payment.
  enforceInvoicePayVerification = "enforceInvoicePayVerification",
  // Security pass 2026-09: when ON, the public "respond to offer" page must send the accepted
  // registration id to fetch a team invite code. Leave OFF until the web client that sends it is
  // deployed (old web bundles do not send it), then turn ON.
  requireRegistrationForNoAuthInviteCode = "requireRegistrationForNoAuthInviteCode",
  // Security pass 2026-09: when ON, only org admins may email arbitrary typed-in addresses
  // (rawEmail recipient groups). OFF (default) keeps today's behavior — the web Compose screen is
  // available to every user and its search box turns any typed address into a rawEmail recipient —
  // while logging a shadow pipe event (metric-security-raw-email-would-be-blocked) so the impact can be
  // measured before flipping.
  restrictRawEmailRecipientsToOrgAdmins = "restrictRawEmailRecipientsToOrgAdmins",
  // Security pass Phase 0 (2026-09-25): turn ON one day after the new web bundle is live everywhere.
  // OFF (default) keeps the legacy behavior old web bundles rely on: getOrgCouponNoAuth returns the coupon maps
  // when playerBundleId is omitted (#4). It does NOT gate the tryout offer token (#2), which is always required
  // on invites whose link carries ?t= (D10).
  legacyWebRetired = "legacyWebRetired",
  // Security pass Phase 0: when ON, PostHook callbacks whose signature does not verify are rejected.
  // OFF (default) only logs metric-security-posthook-signature-mismatch (verify real deliveries first).
  enforcePosthookSignature = "enforcePosthookSignature"
}

// /systemStatus
export interface SystemStatus {
  announcementIsVisible: boolean;
  announcement: string;
  isGlobalMaintenanceModeActive: boolean;
  ios: OsVersionUpdateSettings;
  android: OsVersionUpdateSettings;
  paymentAvailabilityDateMS: number;
  globalFeatureToggles?: { [feature in FeatureToggles]?: true };
  // The following takes precedent over the global feature toggle
  // Meant for very small test groups (since all users will have to download this data). If we want to do large groups testing we should look at another solution.
  smallGroupFeatureToggle: { [accountId: string]: { [feature in FeatureToggles]?: true } };
  pricing: {
    individual: {
      numberCents: number;
      displayString: string;
    };
    family: {
      numberCents: number;
      displayString: string;
    };
    team: {
      numberCents: number;
      displayString: string;
      special?: string;
    };
    org: {
      displayString: string;
    };
  };
}

// /simpleHeartBeat/${accountId}/
export interface SimpleHeartBeat {
  d: number;
  dId: string; // deviceId
}

// /deviceRegistration/${accountId}/
export interface DeviceRegistration {
  accountId: string; // redundant since part of the ref path but useful when exporting
  id: string; // reduant since really just the accountId. FirebasePostgresLift assumes it exists so we duplicate it
  // separated into a node so we can just fetch these pieces when actually sending notifications
  derived?: {
    deviceId: string;
    os: "ios" | "android" | "legacy";
    fcmToken: string;
  };
  os: string;
  deviceId: string;
  deviceName: any;
  token?: string; // fcm or apn token depending if android or ios
  notificationPermissionStatus: any;
  iosPermissions?:
    | {
        badge: boolean;
        alert: boolean;
        sound: boolean;
      }
    | {};
  platform?: any;
  publishTime: string;
  deviceYearClass: any;
  git: string;
  nativeAppVersion: any;
  nativeBuildVersion: any;
  otaManifestId?: string;
  deviceLanguage?: any;
  createdAtMS: number;
  updatedAtMS: number;
}

// /keyValueStore/${k}/
export interface KeyValueStore {
  v: any;
  exp: number; // expiration date in ms
}
